import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BeakerIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  PlayIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  useEndpointTests,
  useEndpointTestRuns,
  useUpdateEndpointTest,
  useDeleteEndpointTest,
} from '@/hooks/useEndpointTests';
import { useCreateEndpointTestRunGroup, useInFlightTestIds } from '@/hooks/useEndpointTestRunGroups';
import { IN_FLIGHT_TOAST_TITLE, inFlightRefusal, inFlightToastDetail } from '@/utils/apiRefusal';
import { useCurrentDeployments, useEnvironments } from '@/hooks/useEnvironments';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useToast } from '@/contexts/ToastContext';
import { DeleteResourceControl } from '@/components/shared/DeleteResourceControl';
import { AddTestDialog } from './AddTestDialog';
import { ImportTestsDialog } from './ImportTestsDialog';
import { ExportTestsMenu } from './ExportTestsMenu';
import { RunToolbar, type RunToolbarOptions } from './RunToolbar';
import { NOTHING_RELEASED, resolveRunTarget, runTargetsBlocked, type RunTargets } from './RunOptionsControls';
import { CriteriaEditor } from './CriteriaEditor';
import { TestingGuideLearnMore } from './TestingGuideLink';
import { SuitesBlock } from './SuitesBlock';
import { SuiteToolbarControls } from './SuiteToolbarControls';
import { SourceBadge } from './SourceBadge';
import { formatRelativeTime } from '@/utils/dateUtils';
import type { Endpoint } from '@/types';
import type { EndpointTest, EndpointTestRun, EndpointTestSuite } from '@/types/endpointTests';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';

interface TestsPanelProps {
  endpoint: Endpoint;
  /**
   * Copy a test's saved input into the runner for ad-hoc tweaking. Optional
   * because the service page's Testing tab has no runner to load into (task
   * 800) — the action is hidden there rather than offered as a dead end.
   */
  onLoadIntoRunner?: (test: EndpointTest) => void;
  /**
   * The generate entry point, if the host has one (task 1185). The panel owns
   * none of its own: the service Testing tab's sections layout passes the
   * Assistant's `GenerateTestsButton`; the Advanced hub and the flag-off
   * Testing tab pass the in-page flow's `OpenGenerateTestsFlowButton`. Called
   * with `header` in the panel header and `empty` in the empty-state card;
   * returning null leaves that place empty.
   */
  renderGenerate?: (placement: 'empty' | 'header') => ReactNode;
  /**
   * A test run was created (task 1176) — the service page opens it in its Test
   * runs list. Absent on the Advanced hub, which has no such list.
   */
  onTestRunCreated?: (testRunId: string) => void;
}

/** Dot color per run status. */
function statusDotClass(status: string): string {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'failed' || status === 'error') return 'bg-red-500';
  return 'bg-amber-500';
}

/** Expanded run history for one test — fetched lazily on expand. */
function TestRunHistory({
  test,
  endpointId,
  expanded,
}: {
  test: EndpointTest;
  endpointId: string;
  expanded: boolean;
}) {
  const { data, isLoading, isError } = useEndpointTestRuns(test.id, expanded);
  const runs = data?.data ?? [];

  if (!expanded) return null;

  return (
    <div className="pl-7 pr-2 pb-2 border-t border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
      {isLoading ? (
        <p className="py-2 text-xs text-gray-500 dark:text-gray-400">Loading runs…</p>
      ) : isError ? (
        <p className="py-2 text-xs text-red-500 dark:text-red-400">Couldn’t load runs.</p>
      ) : runs.length === 0 ? (
        <p className="py-2 text-xs text-gray-500 dark:text-gray-400">No runs yet.</p>
      ) : (
        <ul>
          {runs.map((run: EndpointTestRun) => (
            <li key={run.id} className="flex items-center gap-2 py-1.5 text-xs text-gray-700 dark:text-gray-300">
              <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClass(run.status)}`} />
              <span>{run.status}</span>
              <span className="text-gray-400 dark:text-gray-500">
                {formatRelativeTime(run.createdAt)}
              </span>
              {run.collaborationId && (
                <Link
                  to={serviceRunPath(endpointId, run.collaborationId)}
                  state={{ testId: test.id, testName: test.name }}
                  className="ml-auto text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  View run →
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Edit dialog: name / description / expected outcome / input (JSON). */
function EditTestDialog({ test, onClose }: { test: EndpointTest; onClose: () => void }) {
  const [name, setName] = useState(test.name);
  const [description, setDescription] = useState(test.description ?? '');
  const [expectedOutcome, setExpectedOutcome] = useState(test.expectedOutcome ?? '');
  const [inputText, setInputText] = useState(JSON.stringify(test.input, null, 2));
  const [inputError, setInputError] = useState<string | undefined>();
  const updateTest = useUpdateEndpointTest();
  const toast = useToast();
  // Per-scenario criteria are part of the criteria feature set (task 1215).
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');

  const handleSave = async () => {
    let parsedInput: Record<string, unknown>;
    try {
      parsedInput = JSON.parse(inputText) as Record<string, unknown>;
      setInputError(undefined);
    } catch {
      setInputError('Invalid JSON format');
      return;
    }
    try {
      await updateTest.mutateAsync({
        id: test.id,
        data: {
          name: name.trim(),
          description: description.trim() || null,
          expectedOutcome: expectedOutcome.trim() || null,
          input: parsedInput,
        },
      });
      toast.success('Scenario updated', name.trim());
      onClose();
    } catch (err) {
      toast.error('Failed to update scenario', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-test-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 id="edit-test-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Edit scenario
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="edit-test-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              id="edit-test-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="edit-test-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              id="edit-test-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="edit-test-expected" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expected outcome
            </label>
            <textarea
              id="edit-test-expected"
              value={expectedOutcome}
              onChange={(e) => setExpectedOutcome(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="edit-test-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Input (JSON)
            </label>
            <textarea
              id="edit-test-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              rows={6}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {inputError && <p className="mt-1 text-xs text-red-500">{inputError}</p>}
          </div>
          {criteriaEnabled && (
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Criteria for this scenario
              </span>
              {/* Saved as they are edited, through the criteria routes (task 1180). */}
              <CriteriaEditor scope={{ kind: 'test', endpointTestId: test.id }} />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={updateTest.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || updateTest.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateTest.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The Tests panel (task 365): the persistent, account-scoped list of saved
 * tests for this endpoint, replacing the ephemeral Recent Runs card. Each row
 * shows a source badge and last-run status, with run / load-into-runner /
 * edit / delete actions and an expandable run history linking to the run
 * detail page.
 */
export function TestsPanel({ endpoint, onLoadIntoRunner, renderGenerate, onTestRunCreated }: TestsPanelProps) {
  const { data, isLoading, isError } = useEndpointTests(endpoint.id);
  const createTestRun = useCreateEndpointTestRunGroup();
  const deleteTest = useDeleteEndpointTest();
  const toast = useToast();
  /**
   * The test runs feature set's lever (task 1215): starting a test run (the
   * run toolbar, its selection, a row's Run), suites, Add scenario, Import and
   * Export. Off, the saved scenarios stay listed, editable and deletable.
   */
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  /**
   * Scenario Import and Export (task 1266), off by default (kv, 2026-10-23).
   * Stacked on the runs lever for Import; off, the header closes up to
   * Generate scenarios and Add scenario.
   */
  const importExportEnabled = useFlag('innovate.fe.testing.scenarios.import-export');

  // The target picker follows the same lever the Releases tab reads: the
  // resource-versioning flag AND the endpoint status's `versioningEnabled`
  // (task 1176). Without it every test run targets the working state.
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  const { status: versionStatus } = useResourceVersionStatus('endpoint', endpoint.id);
  const targetsEnabled = versioningFlag && versionStatus.versioningEnabled;
  const { environments, isLoading: environmentsLoading } = useEnvironments({ enabled: targetsEnabled });
  const { data: deployments } = useCurrentDeployments(endpoint.id, { enabled: targetsEnabled });
  // The three-state target rule's inputs (task 1236); null while the
  // versioning feature itself is off, so nothing is said about it — and while
  // the stages and deployments are still loading, so "nothing is released" is
  // never shown for a moment before the releases arrive.
  const targetsLoading = targetsEnabled && (environmentsLoading || deployments === undefined);
  const runTargets: RunTargets | null = versioningFlag && !targetsLoading
    ? {
        versioningEnabled: versionStatus.versioningEnabled,
        publishedVersion: versionStatus.publishedVersion,
        environments: targetsEnabled ? environments : [],
        deployments: targetsEnabled ? deployments ?? [] : [],
      }
    : null;
  const nothingReleased = runTargetsBlocked(runTargets);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [runOptions, setRunOptions] = useState<RunToolbarOptions>({
    environmentId: null,
    concurrency: 1,
  });

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EndpointTest | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const tests = useMemo(() => data?.data ?? [], [data]);
  /**
   * Scenarios already running in a test on the selected target (task 1229):
   * the Run on picker's value, or the working state without a picker. Their
   * row Run is disabled, and so is Run selected while the selection includes
   * any; the service refuses the same start with 409 IN_FLIGHT.
   */
  const inFlight = useInFlightTestIds(endpoint.id, resolveRunTarget(runTargets, runOptions.environmentId));
  const runningCount = tests.filter((test) => inFlight.has(test.id)).length;
  // Selection survives a refetch only for tests that still exist.
  const selected = tests.filter((test) => selectedIds.has(test.id)).map((test) => test.id);
  const allSelected = tests.length > 0 && selected.length === tests.length;

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(tests.map((test) => test.id)));

  /**
   * One test run for `testIds` — the only execution route (task 1176). Every
   * test run is graded against whatever criteria exist (task 1185): grading
   * with no criteria costs nothing, so there is no toggle and no second kind
   * of testing.
   */
  const startTestRun = async (
    testIds: string[],
    target: RunToolbarOptions,
    /** Where the start came from (task 1232); a suite run names its suite in the toast (task 1209). */
    source: { origin: 'selection' | 'single' } | { origin: 'suite'; suite: EndpointTestSuite },
  ): Promise<boolean> => {
    const suiteName = source.origin === 'suite' ? source.suite.name : undefined;
    try {
      const created = await createTestRun.mutateAsync({
        endpointId: endpoint.id,
        testIds,
        environmentId: target.environmentId,
        concurrency: target.concurrency,
        graded: true,
        origin: source.origin,
        ...(source.origin === 'suite' ? { endpointTestSuiteId: source.suite.id } : {}),
      });
      const count = `${testIds.length} ${testIds.length === 1 ? 'scenario' : 'scenarios'}`;
      toast.success('Test started', suiteName ? `${suiteName} · ${count}` : count);
      onTestRunCreated?.(created.id);
      return true;
    } catch (err) {
      const names = inFlightRefusal(err);
      if (names) toast.error(IN_FLIGHT_TOAST_TITLE, inFlightToastDetail(names));
      else toast.error('Test failed to start', err instanceof Error ? err.message : undefined);
      return false;
    }
  };

  // A stale environment (deleted since it was picked) falls back to working state.
  // A stale or non-target pick (the working state once published, a deleted
  // stage) resolves to the default target (task 1236).
  const resolveTarget = (options: RunToolbarOptions): RunToolbarOptions => ({
    environmentId: resolveRunTarget(runTargets, options.environmentId),
    concurrency: options.concurrency,
  });

  const runFromToolbar = async (testIds: string[]) => {
    const started = await startTestRun(testIds, resolveTarget(runOptions), { origin: 'selection' });
    if (started) setSelectedIds(new Set());
  };

  // A suite's Run (task 1209): one test run of its members with the Suites
  // block's own settings; the list's selection is left alone.
  const runSuite = (suite: EndpointTestSuite, options: RunToolbarOptions) =>
    void startTestRun(suite.testIds, resolveTarget(options), { origin: 'suite', suite });

  // A row's own Run: a test run of one, on the working state.
  const handleRun = async (test: EndpointTest) => {
    setRunningId(test.id);
    // On the default target: the working state before the first publish, the
    // `/` release (or built-in default) after it (task 1236).
    await startTestRun([test.id], { environmentId: resolveRunTarget(runTargets, null), concurrency: 1 }, { origin: 'single' });
    setRunningId(null);
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
      data-testid="tests-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BeakerIcon className="h-5 w-5 mr-2 text-gray-400" />
          Scenarios
          {tests.length > 0 && (
            <span className="ml-1.5 text-gray-400" data-testid="tests-count">
              · {runningCount > 0 ? `${runningCount} of ${tests.length} running` : tests.length}
            </span>
          )}
        </h3>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {renderGenerate?.('header')}
          {runsEnabled && importExportEnabled && (
            <button
              type="button"
              onClick={() => setImporting(true)}
              data-testid="tests-import-open"
              className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
              Import
            </button>
          )}
          {importExportEnabled && (
            <ExportTestsMenu
              endpointId={endpoint.id}
              filename={`tests-${endpoint.name || endpoint.path}`.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-')}
              disabled={tests.length === 0}
            />
          )}
          {runsEnabled && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="tests-add-open"
              className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add scenario
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading scenarios…</p>
      ) : isError ? (
        <p className="p-4 text-sm text-red-500 dark:text-red-400">Couldn’t load scenarios.</p>
      ) : tests.length === 0 ? (
        <div className="p-6 text-center" data-testid="tests-empty">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No saved scenarios yet. Generate scenarios from this AI Service’s
            definition, add one by hand, or import a CSV or JSON file.{' '}
            <TestingGuideLearnMore anchor="tests" testId="tests-learn-more" />
          </p>
          {renderGenerate?.('empty')}
        </div>
      ) : (
        <>
        <SuitesBlock
          endpointId={endpoint.id}
          tests={tests}
          targets={runTargets}
          onRun={runSuite}
          running={createTestRun.isPending}
        />
        {runsEnabled && selected.length > 0 && (
          <RunToolbar
            selectedCount={selected.length}
            options={runOptions}
            onOptionsChange={setRunOptions}
            targets={runTargets}
            onRunSelected={() => void runFromToolbar(selected)}
            onClear={() => setSelectedIds(new Set())}
            running={createTestRun.isPending}
            inFlightCount={selected.filter((id) => inFlight.has(id)).length}
          >
            <SuiteToolbarControls endpointId={endpoint.id} selectedIds={selected} />
          </RunToolbar>
        )}
        {runsEnabled && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              aria-label="Select all scenarios"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = selected.length > 0 && !allSelected; }}
              onChange={toggleAll}
              className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>Select scenarios to run them together as one test</span>
          </div>
        )}
        <ul data-testid="tests-list">
          {tests.map((test) => {
            const expanded = expandedId === test.id;
            return (
              <li key={test.id} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <div className="flex items-start gap-2 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  {runsEnabled && (
                    <input
                      type="checkbox"
                      aria-label={`Select ${test.name}`}
                      checked={selectedIds.has(test.id)}
                      onChange={() => toggleSelected(test.id)}
                      className="mt-1 h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : test.id)}
                    aria-label={expanded ? 'Collapse run history' : 'Expand run history'}
                    aria-expanded={expanded}
                    className="mt-0.5 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {expanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {test.name}
                      </span>
                      <SourceBadge source={test.source} />
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                      {inFlight.has(test.id) ? (
                        <span data-testid={`test-in-flight-${test.id}`} className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
                          <span className="w-2 h-2 rounded-full shrink-0 bg-indigo-500 animate-pulse" />
                          running in a test · started {formatRelativeTime(inFlight.get(test.id)!)}
                        </span>
                      ) : test.lastRun ? (
                        <>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClass(test.lastRun.status)}`} />
                          <span>
                            last run {test.lastRun.status} · {formatRelativeTime(test.lastRun.createdAt)}
                          </span>
                        </>
                      ) : (
                        <span>never run</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {runsEnabled && (
                      <button
                        type="button"
                        onClick={() => handleRun(test)}
                        disabled={runningId === test.id || inFlight.has(test.id) || nothingReleased}
                        aria-label="Run scenario"
                        title={
                          nothingReleased
                            ? NOTHING_RELEASED
                            : inFlight.has(test.id)
                              ? 'Already running in a test on this target'
                              : 'Run'
                        }
                        className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 disabled:opacity-50"
                      >
                        <PlayIcon className={`h-4 w-4 ${runningId === test.id ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                    {onLoadIntoRunner && (
                      <button
                        type="button"
                        onClick={() => onLoadIntoRunner(test)}
                        aria-label="Load into runner"
                        title="Load into runner"
                        className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditing(test)}
                      aria-label="Edit scenario"
                      title="Edit"
                      className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    {/* The one delete affordance (task 864), on its own flag
                        like every other resource delete (task 1215). A
                        deleted scenario writes a ledger row, so it can be
                        restored from the Archive. */}
                    <DeleteResourceControl
                      flag="innovate.fe.tests.delete"
                      entityLabel="scenario"
                      name={test.name}
                      id={test.id}
                      body="Its run history will no longer be listed here."
                      onDelete={() => deleteTest.mutateAsync(test.id)}
                      testId={`test-delete-${test.id}`}
                    />
                  </div>
                </div>
                <TestRunHistory test={test} endpointId={endpoint.id} expanded={expanded} />
              </li>
            );
          })}
        </ul>
        </>
      )}

      {editing && <EditTestDialog test={editing} onClose={() => setEditing(null)} />}
      {runsEnabled && adding && <AddTestDialog endpoint={endpoint} onClose={() => setAdding(false)} />}
      {runsEnabled && importExportEnabled && importing && <ImportTestsDialog endpoint={endpoint} onClose={() => setImporting(false)} />}
    </div>
  );
}
